import { Component, ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  // Changes whenever the user navigates. Used purely to clear a displayed
  // error on navigation — it never forces a remount while rendering happily,
  // so the normal (no-error) render path is unchanged.
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  isChunkError: boolean;
  // True once we've decided NOT to auto-reload (reload-loop guard tripped).
  // We then show the fallback UI instead of a blank screen.
  gaveUp: boolean;
  lastResetKey?: string;
}

// Window (in ms) within which a second chunk-error reload is treated as a
// reload loop rather than a fresh failure. Stops us reloading forever if the
// chunk is genuinely missing rather than just stale.
const RELOAD_LOOP_WINDOW_MS = 15_000;
const RELOAD_FLAG_KEY = "chunk_reload_at";

/**
 * Returns true when `error` is a failed dynamic import (code-split chunk).
 *
 * After a deploy the served index.html may still reference the previous
 * build's chunk hashes; navigating to a lazy route (Agent Training,
 * Automation, Conversations, …) then 404s the chunk and React.lazy throws.
 * Suspense does not catch this, so without a boundary the whole tree
 * unmounts to a white screen. See AN-1327.
 */
function isChunkLoadError(error: unknown): boolean {
  const err = error as { name?: string; message?: string } | null;
  const name = err?.name ?? "";
  const message = err?.message ?? "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk \d+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    isChunkError: false,
    gaveUp: false,
    lastResetKey: this.props.resetKey,
  };

  static getDerivedStateFromError(error: unknown): Partial<ErrorBoundaryState> {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  // Clear a displayed error when the user navigates. This only does anything
  // while an error is on screen — with no error it returns null and leaves
  // the normal render path (and React's usual reconciliation) untouched.
  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    if (props.resetKey === state.lastResetKey) {
      return null;
    }
    if (state.hasError) {
      return {
        hasError: false,
        isChunkError: false,
        gaveUp: false,
        lastResetKey: props.resetKey,
      };
    }
    return { lastResetKey: props.resetKey };
  }

  componentDidCatch(error: unknown) {
    if (!isChunkLoadError(error)) {
      console.error("Unhandled render error caught by ErrorBoundary:", error);
      return;
    }
    // Stale chunk after a deploy: a hard reload pulls the fresh index.html +
    // chunk hashes. Guard against an infinite reload loop in case the chunk
    // is genuinely gone (not just stale) — in that case fall through to the
    // fallback UI rather than reloading forever or showing a blank screen.
    const last = Number(sessionStorage.getItem(RELOAD_FLAG_KEY) || 0);
    const now = Date.now();
    if (now - last > RELOAD_LOOP_WINDOW_MS) {
      sessionStorage.setItem(RELOAD_FLAG_KEY, String(now));
      window.location.reload();
    } else {
      this.setState({ gaveUp: true });
    }
  }

  handleReload = () => {
    sessionStorage.removeItem(RELOAD_FLAG_KEY);
    window.location.reload();
  };

  render() {
    const { hasError, isChunkError, gaveUp } = this.state;

    if (!hasError) {
      return this.props.children;
    }

    // Stale-chunk auto-reload is in flight — don't flash the error UI.
    if (isChunkError && !gaveUp) {
      return null;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div className="flex flex-col items-center justify-center gap-4 py-24 text-center">
        <div className="text-lg font-medium">Something went wrong</div>
        <div className="max-w-md text-slate-500">
          This page failed to load. Please reload to try again.
        </div>
        <button
          type="button"
          onClick={this.handleReload}
          className="rounded-md bg-primary px-4 py-2 text-white"
        >
          Reload page
        </button>
      </div>
    );
  }
}

/**
 * Wraps a routed page in {@link ErrorBoundary}, feeding it the current
 * location key so a caught error is cleared when the user navigates away
 * (React reuses the boundary instance across routes, so the error state
 * would otherwise persist and trap the user on the fallback).
 */
function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  return <ErrorBoundary resetKey={location.key}>{children}</ErrorBoundary>;
}

export { RouteErrorBoundary };
export default ErrorBoundary;
